//Imports
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Controls
import {
    IconButton,
    MenuComponent,
    SearchInput,
    TableButtons,
    TableView,
    useTablePagination,
} from "@gpstrackit/gtc-react-controls";
import { Grid } from "@mui/material";

//Components-
import { NotificationManager } from "../../../../components/Notifications";
import Preferences from "Core/containers/Preferences";
import { Timezone } from 'Components/uiControls';


//Helpers
import { GetFormatForMoment, completeColumns } from 'Core/data/Helpers';

//Modals
import CreateEditContact from './modals/createEditContact';
import DeleteContact from './modals/deleteContact';

//Actions
import {
    workOrdersSearchContacts,
    reset
} from "./redux/slice";
import {
    openModalCreateEditContact,
    openModalDeleteContact,
    sendGenericExport,
    savePreference,
    clearRedux
} from "Redux/actions";

//Variables
const KEY_PREFERENCE = "WOContactTab-list";
const ContactTab = ({ customerId, fullHeight }) => {

    //States
    const [selectedItems, setSelectedItems] = useState([]);
    const [preferences, setPreferences] = useState({});

    //Table refs
    const contactTableRef = useRef();

    //Language
    const { messages, locale } = useIntl();

    //Redux States
    const { loading, contacts, total, error } = useSelector((state) => state.workOrdersContactTabRedux);
    const openModalCreateEdit = useSelector((state) => state.workOrdersCreateEditContactRedux.open);
    const openModalDelete = useSelector((state) => state.workOrdersDeleteContactRedux.open);
    const exportResult = useSelector((state) => state.commomRedux.exportResult);
    const exportName = useSelector((state) => state.commomRedux.exportName);
    const { authenticatedId } = useSelector(state => state.securityRedux);
    const user = useSelector((state) => state.securityRedux.user);
    const { dateformat, timeZone } = user;

    const format = GetFormatForMoment(dateformat);

    const dispatch = useDispatch();
    const paginationContacts = useTablePagination({
        dispatch,
        action: workOrdersSearchContacts,
        searchFields: ["id", "name", "email"],
        tableRef: contactTableRef,
        sendGenericExport,
        exportName: "contacts",
        termsFields: [{
            fields: "entityId",
            terms: [+customerId],
            exact: true
        }]
    });

    //Options
    const getCustomerMenuOptions = () => [
        messages["edit"],
        messages["delete"]
    ];

    //Handlers and utils
    const handleClickWorkOrderMenu = (type, contact) => {
        if (type === messages["edit"]) {
            dispatch(openModalCreateEditContact({ customerId: +customerId, contactId: contact?.id }))
        } else if (type === messages["delete"]) {
            dispatch(openModalDeleteContact({ contact: contact }))
        }
    };

    const refreshTable = () => {
        setSelectedItems([]);
        contactTableRef?.current?.setPage(1);
        contactTableRef?.current?.setSelectedItems([]);
        paginationContacts.onChangePage(1);
    };

    const savePreferences = (key, value) => {
        let preferenceValue = { ...preferences, [key]: value }
        setPreferences(preferenceValue)
        dispatch(savePreference({ entityName: "User", entityId: authenticatedId, preferenceKey: KEY_PREFERENCE, preferenceValue: preferenceValue }));
    }

    //Columns
    let currentColumns = [
        {
            key: "name",
            title: messages['workOrder_contactTab_name'],
            functionString: 'return row?.name',
            sort: true,
            sortField: "name",
            show: true,
            export: true,
            width: 200,
            exportValue: (row) => row?.name,
            render: (row) => (
                <Grid container alignItems="center">
                    <Grid item xs={10} style={{ wordWrap: 'break-word', width: 200 }}>
                        <span>{row?.name}</span>
                    </Grid>
                    <Grid item xs={2}>
                        <MenuComponent
                            placement="right-start"
                            options={getCustomerMenuOptions(row?.unit?.label)}
                            icon="more_vert"
                            className="showInHover"
                            onClick={handleClickWorkOrderMenu}
                            value={row}
                        />
                    </Grid>
                </Grid>
            )
        },
        {

            key: "email",
            title: messages['workOrder_contactTab_email'],
            functionString: 'return row?.email',
            show: true,
            export: true,
            sort: true,
            sortField: "email",
            width: 250,
            exportValue: (row) => row?.email,
            render: (row) => <span>{row?.email}</span>
        },
        {

            key: "notifyByEmail",
            title: messages['workOrder_contactTab_notifyByEmail'],
            functionString: `return row?.notifyByEmail ? "YES" : "NO"`,
            show: true,
            export: true,
            sort: true,
            sortField: "notifyByEmail",
            width: 150,
            exportValue: (row) => row?.notifyByEmail ? messages['yes'] : messages['no'],
            render: (row) => <span>{row?.notifyByEmail ? messages['yes'] : messages['no']}</span>
        },
        {

            key: "phone",
            title: messages['workOrder_contactTab_phone'],
            functionString: 'return row?.phone',
            show: true,
            export: true,
            sort: true,
            sortField: "phone",
            width: 150,
            exportValue: (row) => row?.phone,
            render: (row) => <span>{row?.phone}</span>
        },
        {

            key: "notifyByPhone",
            title: messages['workOrder_contactTab_notifyByPhone'],
            functionString: `return row?.notifyByPhone ? "YES" : "NO"`,
            show: true,
            export: true,
            sort: true,
            sortField: "notifyByPhone",
            width: 150,
            exportValue: (row) => row?.notifyByPhone ? messages['yes'] : messages['no'],
            render: (row) => <span>{row?.notifyByPhone ? messages['yes'] : messages['no']}</span>
        },
        {
            key: "createdOn",
            title: messages['workOrder_contactTab_createdOn'],
            functionString: `
                function formatDate(date,tz,formatDate) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).tz(tz).format(formatDate)
                    return formatted;
                }

                let timeZone = "${timeZone}"
                let format = "${format}" + " hh:mm a, z"

                return row?.createdOn ? formatDate(row?.createdOn,timeZone,format) : ''
            `,
            show: true,
            export: true,
            sort: true,
            sortField: "createdOn",
            width: 200,
            exportValue: (row) => row?.createdOn,
            render: (row) => (
                <Timezone
                    date={row?.createdOn}
                    dateFormat={dateformat}
                    timeFormat="hh:mm a"
                    timezone={timeZone}
                />
            )
        }
    ]

    useEffect(() => {
        paginationContacts.setColumns(currentColumns);
        setSelectedItems([]);
        contactTableRef?.current?.setPage(1);
        contactTableRef?.current?.setSelectedItems([]);
    }, []);

    useEffect(() => {
        paginationContacts.refresh({ _sort: { key: "id", order: "asc" } });
        return () => dispatch(reset());
    }, [])

    useEffect(() => {
        if (error) {
            NotificationManager.error(
                messages["errorMessage"],
                messages["error"],
                3000,
                null,
                null,
                "warning"
            );
            dispatch(reset({ error: null }));
        }
        if (exportResult && exportName === "contacts") {
            NotificationManager.info(
                messages["exportInProcessBody"],
                messages["exportInProcessTitle"],
                4000,
                null,
                null,
                "info",
                null
            );
            dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
        }
    }, [error, exportResult, exportName]);

    useEffect(() => {
        if (preferences?.columns?.length) {
            const preferenceMap = new Map(preferences.columns.map(col => [col.key, col]));
            // Reorder columns based on preferences (keeping `show`)
            const reorderedColumns = preferences.columns
                .map(pref => {
                    const col = currentColumns.find(c => c.key === pref.key);
                    return col ? { ...col, show: pref.show } : null;
                })
                .filter(Boolean);
            // Add new columns (not listed in preferences), with `show: true` by default
            const newColumns = currentColumns
                .filter(col => !preferenceMap.has(col.key))
                .map(col => ({ ...col, show: true }));

            const finalColumns = [...reorderedColumns, ...newColumns];
            paginationContacts.setColumns(finalColumns);
            setPreferences({ ...preferences, columns: [] });
        }
    }, [preferences.columns])

    useEffect(() => {
        if (paginationContacts.columns.length > 0) {
            setPreferences({ ...preferences, columns: paginationContacts.columns })
        }
    }, [locale, timeZone, dateformat]);

    return (
        <Fragment>
            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Grid item xs={6} sm={2}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item style={{ display: "flex" }}>
                            <IconButton
                                tooltip={messages["add"]}
                                placement="bottom"
                                variant="secondary"
                                icon="add_box"
                                size="small"
                                disabled={true || loading}
                                onClick={() => {
                                    dispatch(openModalCreateEditContact({ customerId: +customerId }))
                                }}
                            />
                            {
                                selectedItems?.length ? (
                                    <IconButton
                                        tooltip={messages["delete"]}
                                        placement="bottom"
                                        variant="secondary"
                                        icon="delete"
                                        size="small"
                                        onClick={() => {
                                            dispatch(openModalDeleteContact({ contactList: selectedItems }))
                                        }}
                                    />
                                ) : <></>
                            }
                            <TableButtons
                                messages={messages}
                                {...paginationContacts}
                                buttons={["export", "orderColumn"]}
                                onExport={(type) => {
                                    paginationContacts.onExport(type);
                                }}
                                onChangeColumns={(columns) => {
                                    savePreferences("columns", columns)
                                    paginationContacts.setColumns(columns)
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={4} className="mb-10 text-right">
                    <SearchInput
                        placeholder={messages["search"]}
                        searchText={paginationContacts.searchText}
                        onSearchText={paginationContacts.onSearchChange}
                    />
                </Grid>
            </Grid>
            <Preferences
                preferencesKey={KEY_PREFERENCE}
                onInitialPreferences={(data) => {
                    setPreferences(data)
                }}
            >
                <TableView
                    keyColumn="id"
                    loading={loading}
                    topheight={fullHeight ? 280 : 340}
                    data={contacts || []}
                    total={total}
                    exportOrientation="landscape"
                    onSelectedItems={(items) => setSelectedItems(items)}
                    {...paginationContacts}
                    onChangeColumns={(columns) => {
                        let currentColumns = paginationContacts?.columns || []
                        let finalsColumns = completeColumns(currentColumns, columns)
                        savePreferences("columns", finalsColumns)
                        paginationContacts.setColumns(finalsColumns)
                    }}
                    hideAllInPagination
                />
            </Preferences>
            {openModalCreateEdit && <CreateEditContact refresh={refreshTable} />}
            {openModalDelete && <DeleteContact refresh={refreshTable} />}
        </Fragment>
    );
};

export default ContactTab;
